import { defineStore } from "pinia";
import posthog from "posthog-js";
import type { RealtimeState, RealtimeStateChange } from "@signaloid/cloud-compute-engine-client";
import { sdk } from "@/js/sdk";
import { MAX_REALTIME_RECONNECT_ATTEMPTS } from "@/js/realtimeConstants";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { useTasksStore } from "./tasks";
import { useBuildsStore } from "./builds";

export type { RealtimeState };

type StateChangeListener = (change: RealtimeStateChange) => void;
type PausedEpisodeOutcome = "recovered" | "stopped";

export const DEGRADE_AFTER_MS = 30_000;
export const MAX_CONNECT_RETRIES = MAX_REALTIME_RECONNECT_ATTEMPTS;
const FIRST_CONNECT_RETRY_DELAY_MS = 1_000;
const MAX_CONNECT_RETRY_DELAY_MS = 30_000;

type StoreState = {
	connectionState: RealtimeState;
	degraded: boolean;
	stopped: boolean;
	userId: string | undefined;
	stateChangeListener: StateChangeListener | undefined;
	degradeTimer: number | undefined;
	connectRetryTimer: number | undefined;
	connectRetryAttempt: number;
	pausedSince: number | undefined;
	hasChannelFailure: boolean;
};

export const useRealtimeStore = defineStore("realtime", {
	state: (): StoreState => ({
		connectionState: "idle",
		degraded: false,
		stopped: false,
		userId: undefined,
		stateChangeListener: undefined,
		degradeTimer: undefined,
		connectRetryTimer: undefined,
		connectRetryAttempt: 0,
		pausedSince: undefined,
		hasChannelFailure: false,
	}),
	getters: {
		isLive: (state): boolean => state.connectionState === "open" && !state.hasChannelFailure,
		showPausedNotice: (state): boolean => state.degraded && !state.stopped,
		showStoppedNotice: (state): boolean => state.stopped,
	},
	actions: {
		start(userId: string): void {
			if (this.stateChangeListener) return;
			this.userId = userId;
			this.stateChangeListener = (change: RealtimeStateChange) => this.handleStateChange(change);
			sdk.realtime.on("statechange", this.stateChangeListener);
			this.connectionState = sdk.realtime.getState();
			void this.subscribeChannels();
		},
		stop(): void {
			if (this.stateChangeListener) {
				sdk.realtime.off("statechange", this.stateChangeListener);
				this.stateChangeListener = undefined;
			}
			useTasksStore().teardownRealtime();
			useBuildsStore().teardownRealtime();
			this.clearDegradeTimer();
			this.clearConnectRetryTimer();
			this.degraded = false;
			this.stopped = false;
			this.connectRetryAttempt = 0;
			this.pausedSince = undefined;
			this.hasChannelFailure = false;
			this.connectionState = "idle";
		},
		reportChannelFailure(error: unknown): void {
			this.hasChannelFailure = true;
			if (this.stopped) return;
			this.markPausedStart();
			this.scheduleDegrade(
				`Realtime updates paused after a channel failed for ${DEGRADE_AFTER_MS / 1000} s`,
				error,
			);
			this.scheduleConnectRetry();
		},
		async subscribeChannels(): Promise<boolean> {
			if (!this.userId) return false;
			const subscribed = await Promise.all([
				useTasksStore().initRealtime(this.userId),
				useBuildsStore().initRealtime(this.userId),
			]);
			return subscribed.every(Boolean);
		},
		async retryChannels(): Promise<void> {
			const isSubscribed = await this.subscribeChannels();
			if (!this.hasChannelFailure || this.stopped) return;
			if (isSubscribed) this.recoverFromChannelFailure();
			else this.scheduleConnectRetry();
		},
		recoverFromChannelFailure(): void {
			this.hasChannelFailure = false;
			if (this.connectionState !== "open") return;
			this.endOutage();
			this.reconcileActiveLists();
		},
		reconcileActiveLists(): void {
			void useTasksStore().reconcileActiveTasks();
			void useBuildsStore().reconcileActiveBuilds();
		},
		handleStateChange(change: RealtimeStateChange): void {
			if (!this.stateChangeListener) return;
			this.connectionState = change.state;

			switch (change.state) {
				case "open":
					this.handleOpen(change.previous);
					break;
				case "reconnecting":
					this.markPausedStart();
					this.scheduleDegrade(`Realtime updates paused after ${DEGRADE_AFTER_MS / 1000} s of reconnecting`);
					break;
				case "closed":
					this.handleStopped();
					break;
				case "idle":
					if (change.error) this.handleFailedConnect();
					break;
			}
		},
		handleOpen(previousState: RealtimeState): void {
			if (this.hasChannelFailure) {
				this.clearConnectRetryTimer();
				this.connectRetryAttempt = 0;
				this.stopped = false;
				void this.retryChannels();
				return;
			}
			this.endOutage();
			if (previousState === "reconnecting") this.reconcileActiveLists();
		},
		endOutage(): void {
			this.clearDegradeTimer();
			this.clearConnectRetryTimer();
			this.connectRetryAttempt = 0;
			if (this.degraded) this.endPausedEpisode("recovered");
			this.degraded = false;
			this.stopped = false;
			this.pausedSince = undefined;
		},
		markPausedStart(): void {
			if (this.pausedSince === undefined) this.pausedSince = Date.now();
		},
		scheduleDegrade(warningMessage: string, cause?: unknown): void {
			if (this.degraded || this.degradeTimer !== undefined) return;
			this.degradeTimer = window.setTimeout(() => {
				this.degradeTimer = undefined;
				this.degraded = true;
				this.reportPausedWarning(warningMessage, cause);
			}, DEGRADE_AFTER_MS);
		},
		handleFailedConnect(): void {
			this.markPausedStart();
			this.scheduleDegrade(
				`Realtime updates paused after the first connect failed for ${DEGRADE_AFTER_MS / 1000} s`,
			);
			this.scheduleConnectRetry();
		},
		scheduleConnectRetry(): void {
			if (this.connectRetryTimer !== undefined) return;
			if (this.connectRetryAttempt >= MAX_CONNECT_RETRIES) {
				this.handleStopped();
				return;
			}
			const retryDelay = Math.min(
				FIRST_CONNECT_RETRY_DELAY_MS * 2 ** this.connectRetryAttempt,
				MAX_CONNECT_RETRY_DELAY_MS,
			);
			this.connectRetryAttempt += 1;
			this.connectRetryTimer = window.setTimeout(() => {
				this.connectRetryTimer = undefined;
				void this.retryChannels();
			}, retryDelay);
		},
		handleStopped(): void {
			this.clearDegradeTimer();
			this.clearConnectRetryTimer();
			if (this.degraded) this.endPausedEpisode("stopped");
			else if (!this.stopped)
				this.reportPausedWarning("Realtime updates stopped after the SDK gave up reconnecting");
			this.stopped = true;
		},
		reportPausedWarning(message: string, cause?: unknown): void {
			monitoringCaptureError(new Error(message, { cause }), "Realtime reconnect", { severity: "warning" });
		},
		endPausedEpisode(outcome: PausedEpisodeOutcome): void {
			if (this.pausedSince === undefined) return;
			posthog.capture("realtime_updates_paused", {
				duration_seconds: Math.round((Date.now() - this.pausedSince) / 1000),
				outcome,
			});
			this.pausedSince = undefined;
		},
		clearDegradeTimer(): void {
			window.clearTimeout(this.degradeTimer);
			this.degradeTimer = undefined;
		},
		clearConnectRetryTimer(): void {
			window.clearTimeout(this.connectRetryTimer);
			this.connectRetryTimer = undefined;
		},
	},
});
